import React, { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { buildApiUrl } from "./config";
import { fabric } from "fabric";




import * as pdfjs from "pdfjs-dist/build/pdf";

// ✅ Set PDF.js worker - dynamically use the same version as the main library
// This prevents version mismatch errors between API and Worker
try {
  // Option 1: Use the same version as the installed library
  pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;
  
  // Log the versions for debugging
  console.log(`📄 PDF.js API version: ${pdfjs.version}`);
  console.log(`📄 PDF.js W              const assignedForms = homework?.assignedTo ||           // ✅ PRIMARY - from CreateHomeworkComponent
                                homework?.assigned_forms || 
                                homework?.target_forms || 
                                homework?.forms || 
                                homework?.assigned_to || 
                                []; source: https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`);
} catch (error) {
  // Fallback: Use the package.json version
  console.warn('⚠️ Could not detect PDF.js version, using fallback');
  pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@3.4.120/build/pdf.worker.min.js`;
}
const HomeworkAnnotationViewer = () => {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const overrideStudentId = searchParams.get("studentId");
  const localStudentId = localStorage.getItem("studentId") || "anonymous";
  
  // ✅ Add role detection and submission status FIRST
  const userRole = localStorage.getItem("userRole") || "student";
  const teacherEmail = localStorage.getItem("teacherEmail");
  const studentId_local = localStorage.getItem("studentId");
  
  // ✅ ENHANCED: More precise teacher detection
  const isTeacher = (userRole === "teacher" && teacherEmail) || 
                   (window.location.pathname.includes('/teacher') && teacherEmail) ||
                   (searchParams.get('teacherView') === 'true');
  
  // ✅ Force student mode if accessing from student dashboard
  const isStudentAccess = studentId_local && !teacherEmail && userRole !== "teacher";
  const finalIsTeacher = isTeacher && !isStudentAccess;
  
  const [isSubmitted, setIsSubmitted] = useState(false); // Track submission status
  
  // ✅ NEW: Excalidraw-style annotation state
  const [currentTool, setCurrentTool] = useState('pen'); // pen, select, rectangle, ellipse, arrow, text, eraser
  const [strokeColor, setStrokeColor] = useState('#000000');
  const [strokeWidth, setStrokeWidth] = useState(2);
  const [fillColor, setFillColor] = useState('transparent');
  
  // ✅ Simplified tool setup for pen, text, and eraser only
  const setupCanvasToolMode = (fabricInstance) => {
    if (!fabricInstance) return;
    
    // Reset all modes first
    fabricInstance.isDrawingMode = false;
    fabricInstance.selection = false;
    fabricInstance.selectable = false;
    fabricInstance.skipTargetFind = false;
    
    // Remove existing event listeners
    fabricInstance.off('mouse:down');
    fabricInstance.off('mouse:move');
    fabricInstance.off('mouse:up');
    
    switch (currentTool) {
      case 'pen':
        fabricInstance.isDrawingMode = true;
        fabricInstance.freeDrawingBrush = new fabric.PencilBrush(fabricInstance);
        fabricInstance.freeDrawingBrush.color = strokeColor;
        fabricInstance.freeDrawingBrush.width = strokeWidth;
        break;
        
      case 'eraser':
        fabricInstance.isDrawingMode = true;
        fabricInstance.freeDrawingBrush = new fabric.PencilBrush(fabricInstance);
        fabricInstance.freeDrawingBrush.color = '#FFFFFF';
        fabricInstance.freeDrawingBrush.width = strokeWidth * 2;
        fabricInstance.freeDrawingBrush.globalCompositeOperation = 'destination-out';
        break;
        
      case 'text':
        fabricInstance.isDrawingMode = false;
        fabricInstance.selection = true;
        fabricInstance.on('mouse:down', function (options) {
          if (options.target) return;
          const pointer = fabricInstance.getPointer(options.e);
          const text = new fabric.IText("Type here", {
            left: pointer.x,
            top: pointer.y,
            fontSize: 16,
            fill: strokeColor,
            fontFamily: 'Inter, system-ui, sans-serif'
          });
          fabricInstance.add(text);
          fabricInstance.setActiveObject(text);
          text.enterEditing();
        });
        break;
    }
  };
  
  // ✅ Apply tool mode to all canvases
  const applyToolToAllCanvases = () => {
    canvasRefs.current.forEach(canvas => {
      if (canvas && !isReadOnly) {
        setupCanvasToolMode(canvas);
      }
    });
  };
  
  // ✅ Update tool mode when any property changes
  useEffect(() => {
    applyToolToAllCanvases();
  }, [currentTool, strokeColor, strokeWidth, fillColor]);
  
  // ✅ DEBUG: Log role detection
  console.log("🔍 HomeworkAnnotationViewer Role Debug (Enhanced):", {
    userRole,
    isTeacher,
    finalIsTeacher,
    isStudentAccess,
    teacherEmail,
    studentId_local,
    overrideStudentId,
    localStorage_userRole: localStorage.getItem("userRole"),
    localStorage_teacherEmail: localStorage.getItem("teacherEmail"),
    localStorage_studentId: localStorage.getItem("studentId"),
    pathname: window.location.pathname,
    searchParams_teacherView: searchParams.get('teacherView'),
    finalDecision: finalIsTeacher ? "TEACHER MODE" : "STUDENT MODE",
    localStorage_all: Object.keys(localStorage).reduce((acc, key) => {
      acc[key] = localStorage.getItem(key);
      return acc;
    }, {})
  });
  
  // ✅ NEW: Add student selection for teachers
  const [students, setStudents] = useState([]);
  const [selectedStudentId, setSelectedStudentId] = useState(overrideStudentId || "");
  const [homework, setHomework] = useState(null);
  
  // ✅ Updated studentId calculation for teachers vs students
  const studentId = finalIsTeacher ? (selectedStudentId || overrideStudentId) : (overrideStudentId || studentId_local);
  
  // ✅ Determine read-only status: teachers viewing other students OR students who have submitted
  const isReadOnly = (overrideStudentId && overrideStudentId !== studentId_local) || (!finalIsTeacher && isSubmitted);

  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdf, setPdf] = useState(null);
  const [pageCount, setPageCount] = useState(0);
  const containerRef = useRef(null);
  const canvasRefs = useRef([]);
  const lastRenderedStudentId = useRef(null); // ✅ Track last rendered student to prevent duplicates

  // ✅ NEW: Fetch students and homework data for teachers
  useEffect(() => {
    if (finalIsTeacher) {
      console.log("🔍 Teacher detected, fetching student data...");
      
      // Fetch students data
      fetch(`${buildApiUrl("")}/synced-students`)
        .then(res => res.json())
        .then(data => {
          console.log("👥 Students data received:", data);
          setStudents(data.students || []);
        })
        .catch(err => {
          console.error("❌ Failed to fetch students:", err);
          setStudents([]);
        });
        
      // Fetch homework data to get assigned forms
      fetch(`${buildApiUrl("")}/view-homework-by-id/${id}`)
        .then(res => res.json())
        .then(data => {
          console.log("📝 Homework data received:", data);
          console.log("🔍 All homework properties:", Object.keys(data));
          console.log("🎯 Homework data structure analysis:", {
            fullData: data,
            allKeys: Object.keys(data),
            possibleFormKeys: Object.keys(data).filter(key => 
              key.toLowerCase().includes('form') || 
              key.toLowerCase().includes('assign') || 
              key.toLowerCase().includes('target')
            )
          });
          setHomework(data);
        })
        .catch(err => {
          console.error("❌ Failed to fetch homework:", err);
        });
    } else {
      console.log("👤 Student detected, role:", userRole);
    }
  }, [finalIsTeacher, id]);

  // ✅ Handle student selection change for teachers
  const handleStudentChange = (newStudentId) => {
    console.log("👨‍🏫 Teacher selected student:", newStudentId);
    setSelectedStudentId(newStudentId);
    // Update URL parameter to reflect selection
    const newSearchParams = new URLSearchParams(searchParams);
    if (newStudentId) {
      newSearchParams.set('studentId', newStudentId);
    } else {
      newSearchParams.delete('studentId');
    }
    setSearchParams(newSearchParams);
    
    // ✅ CRITICAL FIX: Properly clear canvas refs and container to force re-render
    canvasRefs.current.forEach(canvas => {
      if (canvas && canvas.dispose) {
        canvas.dispose();
      }
    });
    canvasRefs.current = [];
    
    // Clear the container
    if (containerRef.current) {
      containerRef.current.innerHTML = "";
    }
    
    // Add visual feedback
    const selectedStudent = students.find(s => s.school_id === newStudentId);
    if (selectedStudent) {
      console.log("📖 Loading annotations for:", selectedStudent.full_name);
    }
  };

  // ✅ Sync selectedStudentId with URL parameter changes
  useEffect(() => {
    setSelectedStudentId(overrideStudentId || "");
  }, [overrideStudentId]);

  useEffect(() => {
    const loadPdf = async () => {
      const res = await fetch(`${buildApiUrl("")}/view-homework-by-id/${id}`);
      const data = await res.json();
      if (data && data.attachments && data.attachments[0]) {
        const fileUrl = `${buildApiUrl("")}${data.attachments[0]}`;
        setPdfUrl(fileUrl);
        const loadingTask = pdfjs.getDocument(fileUrl);

        const loadedPdf = await loadingTask.promise;
        setPdf(loadedPdf);
        setPageCount(loadedPdf.numPages);
      }
    };
    
    // ✅ Check submission status for students
    const checkSubmissionStatus = async () => {
      if (!finalIsTeacher && !overrideStudentId) {
        try {
          const res = await fetch(`${buildApiUrl("")}/submitted-annotation-time?studentId=${studentId}&homeworkId=${id}`);
          const data = await res.json();
          if (data.submittedAt) {
            setIsSubmitted(true);
          }
        } catch (error) {
          console.log("No submission found yet");
        }
      }
    };
    
    loadPdf();
    checkSubmissionStatus();
  }, [id, studentId, finalIsTeacher, overrideStudentId]);

  useEffect(() => {
    const renderPages = async () => {
      // ✅ Enhanced container readiness checks
      if (!pdf || pageCount === 0) {
        console.log("📄 Waiting for PDF to load...");
        return;
      }
      
      if (!containerRef.current) {
        console.error("❌ Container ref not ready, deferring render");
        return;
      }

      console.log("🎨 Starting to render", pageCount, "pages for studentId:", studentId);
      
      // ✅ CRITICAL FIX: Prevent duplicate rendering
      // Check if we already have rendered canvases and only clear when student changes
      const hasExistingCanvases = canvasRefs.current.length > 0;
      const studentChanged = lastRenderedStudentId.current !== studentId;
      
      if (hasExistingCanvases && !studentChanged) {
        console.log("⚠️ Pages already rendered for same student, skipping duplicate render");
        return;
      }
      
      if (hasExistingCanvases && studentChanged) {
        console.log("🔄 Student changed from", lastRenderedStudentId.current, "to", studentId, "- clearing existing canvases");
        // Properly dispose of existing fabric canvases
        canvasRefs.current.forEach(canvas => {
          if (canvas && canvas.dispose) {
            canvas.dispose();
          }
        });
      }
      
      // Update tracking
      lastRenderedStudentId.current = studentId;
      canvasRefs.current = [];
      
      // ✅ Safely clear container content
      try {
        containerRef.current.innerHTML = "";
      } catch (error) {
        console.error("❌ Error clearing container:", error);
        return;
      }

      for (let pageNum = 1; pageNum <= pageCount; pageNum++) {
        const page = await pdf.getPage(pageNum);
        const viewport = page.getViewport({ scale: 1.2 });
        
        // Calculate display dimensions (max 600px width for responsive design)
        const maxDisplayWidth = 600;
        const scale = Math.min(1, maxDisplayWidth / viewport.width);
        const displayWidth = viewport.width * scale;
        const displayHeight = viewport.height * scale;

        // Create PDF canvas (rendered at full resolution, displayed smaller)
        const canvas = document.createElement("canvas");
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        canvas.style.width = `${displayWidth}px`;
        canvas.style.height = `${displayHeight}px`;
        const context = canvas.getContext("2d");

        // Create annotation canvas (matches display size)
        const fabricCanvas = document.createElement("canvas");
        fabricCanvas.width = displayWidth;
        fabricCanvas.height = displayHeight;

        // Create page wrapper - PDF left, canvas right
        const pageWrapper = document.createElement("div");
        pageWrapper.className = "flex flex-col lg:flex-row gap-4 mb-8 p-4 border border-gray-200 rounded-lg bg-white";
        pageWrapper.id = `page-wrapper-${pageNum}`;
        
        // PDF container (left side)
        const pdfContainer = document.createElement("div");
        pdfContainer.className = "flex-1 w-full lg:w-1/2";
        
        const pdfLabel = document.createElement("h3");
        pdfLabel.textContent = `📄 PDF Page ${pageNum}`;
        pdfLabel.className = "text-lg font-semibold mb-2 text-gray-700";
        
        const pdfCanvasWrapper = document.createElement("div");
        pdfCanvasWrapper.className = "border border-gray-300 rounded overflow-hidden mx-auto";
        pdfCanvasWrapper.style.width = `${displayWidth}px`;
        pdfCanvasWrapper.style.height = `${displayHeight}px`;
        
        // Canvas container (right side)
        const canvasContainer = document.createElement("div");
        canvasContainer.className = "flex-1 w-full lg:w-1/2";
        
        const canvasLabel = document.createElement("h3");
        canvasLabel.textContent = `✏️ Notes Page ${pageNum}`;
        canvasLabel.className = "text-lg font-semibold mb-2 text-gray-700";
        
        // Simple toolbar with only pen, text, and eraser
        let toolsContainer = null;
        if (!isReadOnly) {
          toolsContainer = document.createElement("div");
          toolsContainer.className = "flex items-center gap-2 mb-3 p-2 bg-gray-50 border border-gray-200 rounded";
          
          // Tool buttons
          const tools = [
            { id: 'pen', icon: '✎', title: 'Pen' },
            { id: 'text', icon: 'T', title: 'Text' },
            { id: 'eraser', icon: '⌫', title: 'Eraser' }
          ];
          
          tools.forEach(tool => {
            const btn = document.createElement("button");
            btn.innerHTML = tool.icon;
            btn.title = tool.title;
            btn.className = `w-8 h-8 rounded flex items-center justify-center text-sm font-medium transition-colors ${
              currentTool === tool.id 
                ? 'bg-blue-500 text-white' 
                : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-100'
            }`;
            btn.onclick = () => {
              setCurrentTool(tool.id);
              // Update all canvases
              canvasRefs.current.forEach(canvas => {
                if (canvas && !isReadOnly) {
                  setupCanvasToolMode(canvas);
                }
              });
            };
            toolsContainer.appendChild(btn);
          });
          
          // Clear button
          const clearBtn = document.createElement("button");
          clearBtn.innerHTML = "🗑";
          clearBtn.title = "Clear";
          clearBtn.className = "w-8 h-8 rounded bg-red-50 text-red-600 border border-red-200 hover:bg-red-100 transition-colors flex items-center justify-center ml-2";
          clearBtn.onclick = () => {
            if (confirm(`Clear all notes on page ${pageNum}?`)) {
              const canvas = canvasRefs.current[pageNum - 1];
              if (canvas) canvas.clear();
            }
          };
          
          toolsContainer.appendChild(clearBtn);
          canvasContainer.appendChild(toolsContainer);
        }
        
        const canvasWrapper = document.createElement("div");
        canvasWrapper.className = "border border-gray-300 rounded relative bg-white mx-auto";
        canvasWrapper.style.width = `${displayWidth}px`;
        canvasWrapper.style.height = `${displayHeight}px`;
        canvasWrapper.id = `canvas-wrapper-${pageNum}`;

        // Assemble the structure
        pdfCanvasWrapper.appendChild(canvas);
        pdfContainer.appendChild(pdfLabel);
        pdfContainer.appendChild(pdfCanvasWrapper);
        
        canvasWrapper.appendChild(fabricCanvas);
        canvasContainer.appendChild(canvasLabel);
        canvasContainer.appendChild(canvasWrapper);
        
        pageWrapper.appendChild(pdfContainer);
        pageWrapper.appendChild(canvasContainer);
        
        // ✅ Add null check for containerRef before appending
        if (containerRef.current) {
          containerRef.current.appendChild(pageWrapper);
        } else {
          console.error("❌ containerRef.current is null during page", pageNum, "render - component may have unmounted");
          return;
        }

        // Render the PDF page
        await page.render({ canvasContext: context, viewport }).promise;

        // Initialize Fabric.js canvas
        const fabricInstance = new fabric.Canvas(fabricCanvas, {
          isDrawingMode: !isReadOnly,
          selection: !isReadOnly,
          skipTargetFind: isReadOnly,
          selectable: !isReadOnly,
        });

        // Apply current tool mode to this canvas
        if (!isReadOnly) {
          setupCanvasToolMode(fabricInstance);
        }

        canvasRefs.current.push(fabricInstance);

        // Load existing annotations
        try {
          console.log(`🎨 Loading annotations for studentId: ${studentId}, homeworkId: ${id}, page: ${pageNum}`);
          const res = await fetch(`${buildApiUrl("")}/load-annotation?studentId=${studentId}&homeworkId=${id}&page=${pageNum}`);
          const data = await res.json();
          console.log(`📊 Annotation data for page ${pageNum}:`, data);
          
          if (data && data.canvasJson) {
            console.log(`✅ Loading canvas JSON for page ${pageNum}`);
            fabricInstance.loadFromJSON(data.canvasJson, fabricInstance.renderAll.bind(fabricInstance));
          } else {
            console.log(`📝 No annotations found for page ${pageNum}`);
          }
          
          // Fetch submission time for teacher view, only on first page
          if (finalIsTeacher && selectedStudentId && pageNum === 1) {
            console.log(`⏰ Fetching submission time for student ${selectedStudentId}`);
            fetch(`${buildApiUrl("")}/submitted-annotation-time?studentId=${studentId}&homeworkId=${id}`)
              .then(res => res.json())
              .then(data => {
                console.log(`🕐 Submission time data:`, data);
                const label = document.getElementById("submittedTimeLabel");
                if (label) {
                  if (data.submittedAt) {
                    const date = new Date(data.submittedAt).toLocaleString();
                    label.innerText = `✅ Submitted at ${date}`;
                    label.className = "text-sm text-green-600 font-medium";
                  } else {
                    label.innerText = `⏳ No submission yet`;
                    label.className = "text-sm text-orange-600 font-medium";
                  }
                } else {
                  console.log("❌ submittedTimeLabel element not found");
                }
              })
              .catch(err => {
                console.error("❌ Error fetching submission time:", err);
                const label = document.getElementById("submittedTimeLabel");
                if (label) {
                  label.innerText = `❌ Error loading status`;
                  label.className = "text-sm text-red-600 font-medium";
                }
              });
          }
        } catch (e) {
          console.warn("No previous annotations", e);
        }
      }
    };
    renderPages();
  }, [pdf, pageCount, id, studentId, selectedStudentId]); // ✅ Added selectedStudentId to dependencies

  useEffect(() => {
    const interval = setInterval(() => {
      canvasRefs.current.forEach((fabricInstance, index) => {
        const canvasJson = JSON.stringify(fabricInstance);
        fetch(`${buildApiUrl("")}/save-annotation`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            studentId,
            homeworkId: id,
            page: index + 1,
            canvasJson,
          }),
        }).catch(err => console.error("Auto-save failed:", err));
      });
    }, 10000);

    return () => clearInterval(interval);
  }, [id, studentId]);

  return (
    <>
      <div className="p-4 overflow-auto h-screen bg-gray-50">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => {
                // Return to appropriate dashboard based on user role
                if (finalIsTeacher) {
                  window.location.href = "/teacher";
                } else {
                  window.location.href = "/student-dashboard";
                }
              }}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors duration-200"
              title={`Return to ${finalIsTeacher ? 'Teacher' : 'Student'} Dashboard`}
            >
              <span className="text-lg">←</span>
              <span className="text-sm font-medium">Back to Dashboard</span>
            </button>
            <h1 className="text-2xl font-bold">Homework Annotation Viewer</h1>
          </div>
          
          {/* ✅ Student selector for teachers with dynamic data */}
          {finalIsTeacher && (
            <div className="flex items-center gap-4 bg-yellow-50 p-4 rounded-lg border border-yellow-200">
              <div className="flex items-center gap-2">
                <span className="text-2xl">👩‍🏫</span>
                <div>
                  <label htmlFor="studentIdSelect" className="text-sm font-medium text-gray-700">
                    Select Student to View:
                  </label>
                  {(() => {
                    const assignedForms = homework?.assigned_forms || 
                                         homework?.target_forms || 
                                         homework?.forms || 
                                         homework?.assigned_to || 
                                         [];
                    return assignedForms.length > 0 && (
                      <div className="text-xs text-blue-600 mt-1">
                        📚 Assigned to: {assignedForms.join(', ')}
                      </div>
                    );
                  })()}
                </div>
              </div>
              <select
                id="studentIdSelect"
                className="flex-1 max-w-xs border border-gray-300 px-3 py-2 rounded-lg bg-white shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={selectedStudentId}
                onChange={(e) => handleStudentChange(e.target.value)}
              >
                <option value="">(Select a student to view their work)</option>
                {(() => {
                  // ✅ Enhanced filtering - check multiple possible property names for assigned forms
                  // Based on CreateHomeworkComponent.js, the main property is 'assignedTo'
                  const assignedForms = homework?.assignedTo ||           // ✅ PRIMARY - from CreateHomeworkComponent
                                       homework?.assigned_forms || 
                                       homework?.target_forms || 
                                       homework?.forms || 
                                       homework?.assigned_to || 
                                       homework?.targetForms ||  // camelCase
                                       homework?.assignedForms || // camelCase
                                       homework?.class || 
                                       homework?.classes ||
                                       (homework?.form ? [homework.form] : []) || // single form
                                       [];
                  
                  const filteredStudents = students.filter(student => 
                    assignedForms.length === 0 || assignedForms.includes(student.form)
                  );
                  
                  console.log("👥 Enhanced Student filtering (with assignedTo priority):", {
                    totalStudents: students.length,
                    assignedForms,
                    filteredStudents: filteredStudents.length,
                    homeworkData: homework,
                    primaryProperty: homework?.assignedTo,
                    possibleFormProperties: {
                      assignedTo: homework?.assignedTo,           // ✅ PRIMARY
                      assigned_forms: homework?.assigned_forms,
                      target_forms: homework?.target_forms,
                      forms: homework?.forms,
                      assigned_to: homework?.assigned_to,
                      targetForms: homework?.targetForms,
                      assignedForms: homework?.assignedForms,
                      class: homework?.class,
                      classes: homework?.classes,
                      form: homework?.form
                    },
                    sampleStudent: students[0],
                    firstFewStudents: students.slice(0, 3).map(s => ({ name: s.full_name, form: s.form }))
                  });
                  
                  // ✅ FORCE FILTER: If we still don't have forms assigned, show a debug option
                  if (assignedForms.length === 0 && homework) {
                    console.warn("⚠️ NO ASSIGNED FORMS FOUND! Homework keys:", Object.keys(homework));
                    console.warn("⚠️ Full homework object:", homework);
                  }
                  
                  if (filteredStudents.length === 0) {
                    return students.length === 0 ? (
                      <option disabled>Loading students...</option>
                    ) : (
                      <option disabled>
                        {assignedForms.length > 0 
                          ? `No students found for assigned forms: ${assignedForms.join(', ')}`
                          : "No form assignment found in homework data - showing all students for debugging"
                        }
                      </option>
                    );
                  }
                  
                  return filteredStudents.map((student) => (
                    <option key={student.school_id} value={student.school_id}>
                      {student.full_name} ({student.form}) - ID: {student.school_id}
                    </option>
                  ));
                })()}
              </select>
              {selectedStudentId && (
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    {(() => {
                      const selectedStudent = students.find(s => s.school_id === selectedStudentId);
                      return (
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-xs text-blue-700">
                          {selectedStudent?.full_name?.split(' ').map(n => n[0]).join('').toUpperCase()}
                        </div>
                      );
                    })()}
                    <span className="text-sm text-blue-600 font-medium">
                      👁️ Viewing: {students.find(s => s.school_id === selectedStudentId)?.full_name}
                    </span>
                    <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                      Form: {students.find(s => s.school_id === selectedStudentId)?.form}
                    </span>
                  </div>
                  <span className="text-sm text-gray-500" id="submittedTimeLabel">
                    ⏱ Loading submission status...
                  </span>
                  <button
                    onClick={() => {
                      console.log("🔄 Manually refreshing annotations...");
                      canvasRefs.current = [];
                      // Force re-render by updating a state
                      setSelectedStudentId(selectedStudentId);
                    }}
                    className="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
                    title="Refresh annotations"
                  >
                    🔄 Refresh
                  </button>
                </div>
              )}
            </div>
          )}
          
          {/* ✅ Show submission status for students */}
          {!finalIsTeacher && isSubmitted && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-green-600 font-medium">
                ✅ Assignment Submitted
              </span>
            </div>
          )}
        </div>
        
        {/* ✅ Teacher guidance when no student selected */}
        {finalIsTeacher && !selectedStudentId && (
          <div className="mb-6 p-6 bg-yellow-50 border border-yellow-200 rounded-lg text-center">
            <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">👩‍🏫</span>
            </div>
            <h3 className="text-lg font-semibold text-yellow-800 mb-2">Select a Student</h3>
            <p className="text-yellow-700 mb-2">
              Choose a student from the dropdown above to view their PDF assignment and annotations.
            </p>
            {(() => {
              const assignedForms = homework?.assigned_forms || 
                                   homework?.target_forms || 
                                   homework?.forms || 
                                   homework?.assigned_to || 
                                   [];
              return assignedForms.length > 0 && (
                <p className="text-sm text-blue-700 bg-blue-50 px-3 py-2 rounded-lg inline-block">
                  📚 This homework is assigned to: <strong>{assignedForms.join(', ')}</strong>
                </p>
              );
            })()}
          </div>
        )}
        
        {/* ✅ Only show PDF content when student is selected (for teachers) or always (for students) */}
        {(!finalIsTeacher || selectedStudentId) && (
          <>
            {/* Instructions for side-by-side layout */}
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                📖 <strong>PDF Annotation:</strong> Original PDF on the left, your notes on the right.
                {!isReadOnly && " Use the simple toolbar to write, add text, or erase."}
                {isReadOnly && isSubmitted && " (Read-only: Assignment already submitted)"}
                {isReadOnly && finalIsTeacher && " (Teacher view: Read-only)"}
              </p>
            </div>
            
            <div ref={containerRef} className="space-y-6" />
            
            {/* Submit button - only show for students who haven't submitted yet */}
            {(() => {
              // ✅ ENHANCED: Use the improved teacher detection logic
              const shouldShowSubmit = !finalIsTeacher && !isSubmitted && pageCount > 0 && !overrideStudentId;
              
              console.log("🎯 Submit Button Debug (Enhanced v2):", {
                isTeacher,
                finalIsTeacher,
                isStudentAccess,
                overrideStudentId,
                isSubmitted,
                pageCount,
                shouldShowSubmit,
                userRole,
                teacherEmail,
                studentId_local,
                pathname: window.location.pathname,
                reason: !shouldShowSubmit ? (
                  finalIsTeacher ? "Teacher mode detected" :
                  isSubmitted ? "Already submitted" :
                  pageCount === 0 ? "No pages loaded" :
                  overrideStudentId ? "Viewing another student" :
                  "Unknown reason"
                ) : "Should show submit button"
              });
              
              return shouldShowSubmit;
            })() && (
              <div className="mt-8 text-center p-6 bg-white border border-gray-200 rounded-lg">
                <h3 className="text-lg font-semibold mb-4 text-gray-800">Ready to Submit?</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Make sure you've completed all annotations on all {pageCount} page{pageCount > 1 ? 's' : ''} before submitting.
                </p>
                <button
                  onClick={async () => {
                    try {
                      for (let i = 0; i < canvasRefs.current.length; i++) {
                        const canvasJson = JSON.stringify(canvasRefs.current[i]);
                        await fetch(`${buildApiUrl("")}/submit-annotation`, {
                          method: "POST",
                          headers: {
                            "Content-Type": "application/json",
                          },
                          body: JSON.stringify({
                            studentId,
                            homeworkId: id,
                            page: i + 1,
                            canvasJson,
                          }),
                        });
                      }
                      alert("Homework submitted successfully!");
                      setIsSubmitted(true); // Update the state to hide the button
                      // Optionally reload the page to show the updated UI
                      window.location.reload();
                    } catch (err) {
                      console.error("Submission failed:", err);
                      alert("Submission failed. Please try again.");
                    }
                  }}
                  className="px-8 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold text-lg"
                >
                  ✅ Submit Assignment
                </button>
              </div>
            )}
            
            {/* Show submission confirmation for students who have already submitted */}
            {!finalIsTeacher && !overrideStudentId && isSubmitted && pageCount > 0 && (
              <div className="mt-8 text-center p-6 bg-green-50 border border-green-200 rounded-lg">
                <h3 className="text-lg font-semibold mb-2 text-green-800">Assignment Submitted! ✅</h3>
                <p className="text-sm text-green-600">
                  Your homework has been successfully submitted. You can still view your annotations but cannot make changes.
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default HomeworkAnnotationViewer;
